import {
  faDownload,
  faFilter,
  faSlidersSimple,
} from '@fortawesome/pro-regular-svg-icons'
import { orderBy } from 'lodash'
import { DateTime } from 'luxon'
import { useCallback, useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import LayoutContext from 'shared/contexts/LayoutContext'
import useFilterBadges, {
  UseFilterBadgesHookProps,
} from 'shared/hooks/useFilterBadges'
import { ReturnProps as DateFilterState } from 'shared/hooks/useIWRelativeDatePicker'
import { getUniqueValuesByKey } from 'shared/utils/filterBadgesUtils'
import styled from 'styled-components'
import { formatRelativeDate } from 'tools/insightsManager/helpers'
import {
  buildRangeFilter,
  getSpecialDate,
} from 'tools/insightsManager/utils/filtersToDruid'
import { ReactComponent as NoPodsImg } from '../../../images/no-pods.svg'
import IWButton from './IWButton'
import IWButtonGroup from './IWButtonGroup'
import IWContextMenu from './IWContextMenu'
import IWContextMenuItem from './IWContextMenuItem'
import { DropdownValueProps } from './IWDropdown'
import IWFilterBadgesOptions from './IWFilterBadgeOptions'
import IWFilterBadgesRange, {
  FilterBadgesRangeState,
} from './IWFilterBadgeRange'
import IWFilterBadgesDatetime from './IWFilterBadgesDatetime'
import IWFilterBadgesGroup from './IWFilterBadgesGroup'
import IWModal, {
  IWModalContent,
  IWModalFooter,
  IWModalHeader,
  StyledActions,
} from './IWModal'
import IWPaginationControls from './IWPaginationControls'
import IWTable, { Column, PinDirection, SortDirection } from './IWTable'
import IWToggle from './IWToggle'
import IWToggleGroup from './IWToggleGroup'
import IWTypography from './IWTypography'

const StyledTableWrapper = styled.div``

const PaginationWrapper = styled.div<{ hasSettings?: boolean }>`
  background-color: ${(props) => props.theme.palette.grey[100]};
  padding: 0.5rem;
  justify-content: space-between;
  display: flex;
  flex-direction: ${(props) => !props.hasSettings && 'row-reverse'};
`

const EmptyDiv = styled.div`
  width: 100%;
  margin: 3rem 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  text-align: center;
  align-items: center;

  & > * {
    width: 400px;
  }
`

const StyledIWModalContent = styled(IWModalContent)`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`

const StyledModalToggleTextButtons = styled.span`
  display: flex;
  gap: 0.5rem;
`

const StyledFilterWrapper = styled.div`
  display: flex;
  margin-bottom: 0.5rem;
  justify-content: space-between;
`

const StyledFilterTextWrapper = styled.div<{ active?: boolean }>`
  display: flex;
  margin-top: ${(props) => (props.active ? '1rem' : '0')};
  margin-bottom: ${(props) => (props.active ? '0.75rem' : '0')};
`

const DEBOUNCE_TIME = 500 // ms

type PinnedColumns = { [accessor: string]: PinDirection | undefined }

type ColumnIsInView = {
  [columnName: string]: Partial<GenericTableColumn> & {
    isInView: boolean
    disableToggle?: boolean
  }
}

type ColumnConfig = {
  title: string
  accessor: string
  align: 'right' | 'left' | 'center'
  isInView: boolean
  disableToggle: boolean
}

export type ColumnSelectorProps = {
  isOpen: boolean
  initialColumns: ColumnIsInView
  onClose: () => void
  onConfirm: (columns: ColumnIsInView) => void
  viewableColumns: ColumnIsInView
  columns: GenericTableColumn[]
  onUpdateViewableColumns: (columns: ColumnIsInView) => void
}

export type GenericTableColumn = Column & {
  filterType?: 'string' | 'number' | 'datetime'
  disableToggle?: boolean
}

type GenericTableProps<T> = {
  border?: boolean
  items: T[]
  columns: GenericTableColumn[]
  isLoading?: boolean
  pinnedColumns?: PinnedColumns
  initialHiddenColumns?: string[]
  showColumnSettings?: boolean
  showEmptyMessage?: boolean
  topRightComponent?: React.ReactNode
  onDownload?: (items: T[]) => void
  onTableItemsUpdated?: (items: T[]) => void
}

type UseIWGenericTableHook<T> = {
  itemsInView: T[]
  setItemsInView: (items: T[]) => void
  currentPage: number
  setCurrentPage: (pageNumber: number) => void
  itemsPerPage: number
  setItemsPerPage: (itemsPerPage: number) => void
  setTableItems: (items: T[]) => void
}

const TableEmptyMessage = ({ message }: { message: string }) => {
  return (
    <EmptyDiv data-testid="ls-empty-message">
      <NoPodsImg />
      <IWTypography size="sm" fontHue={{ color: 'grey', value: 500 }}>
        {message}
      </IWTypography>
    </EmptyDiv>
  )
}

function filterColumns(
  inView: ColumnIsInView,
  originalColumns: GenericTableColumn[],
): ColumnConfig[] {
  return originalColumns
    .filter((col: GenericTableColumn) => inView[col.accessor].isInView)
    .map((col: GenericTableColumn) => inView[col.accessor]) as ColumnConfig[]
}

function buildDateFilter(
  dimension: string,
  state: DateFilterState,
  { minTime, maxTime }: { minTime: DateTime; maxTime: DateTime },
  timezone: string = 'UTC',
) {
  const relativeDate = formatRelativeDate(state)
  const timezoneOption = { zone: timezone }

  if (relativeDate.main === 'on') {
    const filter = buildRangeFilter(dimension, {
      selectedFirstConditionOption: { value: '>=', label: '' },
      firstConditionValue: DateTime.fromISO(
        relativeDate.specificDate,
        timezoneOption,
      ).toMillis(),
      selectedSecondConditionOption: { value: '<', label: '' },
      secondConditionValue: DateTime.fromISO(
        relativeDate.specificDate,
        timezoneOption,
      )
        .plus({ day: 1 })
        .toMillis(),
    })
    return { filter }
  }

  if (relativeDate.main === 'after') {
    const filter = buildRangeFilter(dimension, {
      selectedFirstConditionOption: { value: '>=', label: '' },
      firstConditionValue: DateTime.fromISO(
        relativeDate.specificDate,
        timezoneOption,
      ).toMillis(),
    })
    return { filter }
  }

  if (relativeDate.main === 'before') {
    const filter = buildRangeFilter(dimension, {
      selectedFirstConditionOption: { value: '<=', label: '' },
      firstConditionValue: DateTime.fromISO(
        relativeDate.specificDate,
        timezoneOption,
      ).toMillis(),
    })
    return { filter }
  }

  if (relativeDate.main === 'between') {
    const filter = buildRangeFilter(dimension, {
      selectedFirstConditionOption: { value: '>=', label: '' },
      firstConditionValue: DateTime.fromISO(
        relativeDate.datePickerMin,
        timezoneOption,
      ).toMillis(),
      selectedSecondConditionOption: { value: '<=', label: '' },
      secondConditionValue: DateTime.fromISO(
        relativeDate.datePickerMax,
        timezoneOption,
      ).toMillis(),
    })
    return { filter }
  }

  if (relativeDate.main === 'previous') {
    const specialDate = getSpecialDate({ relativeDate, minTime, maxTime })

    if (!specialDate) {
      return undefined
    }

    const filter = buildRangeFilter(dimension, {
      selectedFirstConditionOption: { value: '>=', label: '' },
      firstConditionValue: specialDate
        .setZone(timezone)
        .minus({ [relativeDate.timeLength]: relativeDate.timeLengthValue })
        .toMillis(),
      selectedSecondConditionOption: { value: '<=', label: '' },
      secondConditionValue: specialDate.setZone(timezone).toMillis(),
    })
    return { filter }
  }

  if (relativeDate.main === 'next') {
    const specialDate = getSpecialDate({ relativeDate, minTime, maxTime })

    if (!specialDate) {
      return undefined
    }

    const filter = buildRangeFilter(dimension, {
      selectedFirstConditionOption: { value: '<=', label: '' },
      firstConditionValue: specialDate
        .setZone(timezone)
        .plus({ [relativeDate.timeLength]: relativeDate.timeLengthValue })
        .toMillis(),
      selectedSecondConditionOption: { value: '>=', label: '' },
      secondConditionValue: specialDate.setZone(timezone).toMillis(),
    })
    return { filter }
  }

  return undefined
}

function applyFilters<T>(
  items: T[],
  filters: UseFilterBadgesHookProps,
  timezone: string = 'UTC',
): T[] {
  const filtersWithValues = Object.fromEntries(
    Object.entries(filters).filter(
      ([, value]) => value?.value && value.isInView,
    ),
  )

  if (Object.keys(filtersWithValues)?.length < 0) {
    return items
  }

  const filteredItems = items.filter((item) => {
    let shouldInclude = true

    for (const [key, value] of Object.entries(filtersWithValues)) {
      const columnData = item[key]
      const { type, value: currentValue } = value

      if (type === 'number') {
        const {
          firstConditionValue,
          secondConditionValue,
          selectedFirstConditionOption,
          selectedSecondConditionOption,
        } = currentValue as FilterBadgesRangeState

        if (firstConditionValue == null && secondConditionValue == null) {
          shouldInclude = true
          continue
        }

        const firstValue = firstConditionValue
          ? Number(firstConditionValue)
          : null
        const secondValue = secondConditionValue
          ? Number(secondConditionValue)
          : null

        if (
          (firstValue !== null && isNaN(firstValue)) ||
          (secondValue !== null && isNaN(secondValue))
        ) {
          shouldInclude = true
          continue
        }

        const firstOperator = selectedFirstConditionOption?.value
        const secondOperator = selectedSecondConditionOption?.value
        const isFirstGreaterThan = firstOperator === '>'
        const isSecondLessThan = secondOperator === '<'

        if (firstValue !== null && secondValue !== null) {
          shouldInclude =
            isFirstGreaterThan && isSecondLessThan
              ? columnData > firstValue && columnData < secondValue
              : isFirstGreaterThan
              ? columnData > firstValue && columnData <= secondValue
              : isSecondLessThan
              ? columnData >= firstValue && columnData < secondValue
              : columnData >= firstValue && columnData <= secondValue
          continue
        }

        if (firstValue !== null) {
          shouldInclude = isFirstGreaterThan
            ? columnData > firstValue
            : columnData >= firstValue
          continue
        }

        if (secondValue !== null) {
          shouldInclude = isSecondLessThan
            ? columnData < secondValue
            : columnData <= secondValue
          continue
        }
      }

      if (type === 'string') {
        const { values: filterValue, optionType } = currentValue ?? {}

        if (!filterValue) {
          continue
        }

        const isValueIncluded = filterValue.includes(columnData)
        if (
          (optionType === 'include' && !isValueIncluded) ||
          (optionType === 'exclude' && isValueIncluded)
        ) {
          shouldInclude = false
          continue
        }
      }

      if (type === 'datetime') {
        const parsedDate = DateTime.fromISO(columnData)

        if (!parsedDate.isValid) {
          console.warn(parsedDate.invalidExplanation)
          continue
        }

        const minTime = DateTime.now().setZone(timezone).minus({ weeks: 5000 })
        const maxTime = DateTime.now().setZone(timezone).plus({ weeks: 5000 })

        const columnDataInMillis = parsedDate.setZone(timezone).toMillis()
        const filterOptions = buildDateFilter(
          key,
          currentValue as DateFilterState,
          {
            minTime,
            maxTime,
          },
          timezone,
        )

        if (filterOptions) {
          const { filter: { lower, lowerStrict, upper, upperStrict } = {} } =
            filterOptions

          if (lower && upper) {
            const lowerBound = lowerStrict
              ? columnDataInMillis <= lower
              : columnDataInMillis < lower

            const upperBound = upperStrict
              ? columnDataInMillis >= upper
              : columnDataInMillis > upper

            if (lowerBound || upperBound) {
              shouldInclude = false
              continue
            }
          }
        }
      }
    }

    return shouldInclude
  })

  return filteredItems
}

const ColumnSelector = ({
  isOpen,
  onClose,
  onConfirm,
  viewableColumns,
  columns,
  onUpdateViewableColumns,
}: ColumnSelectorProps) => {
  const { t } = useTranslation()

  const handleToggleAll = (toggle: boolean) => {
    const currentColumns = Object.entries(viewableColumns).reduce(
      (acc, [key, value]) => ({
        ...acc,
        [key]: {
          ...value,
          isInView: viewableColumns[key].disableToggle ? true : toggle,
          disableToggle: viewableColumns[key].disableToggle,
        },
      }),
      {} as ColumnIsInView,
    )

    onUpdateViewableColumns(currentColumns)
  }

  const handleColumnToggle = (columnName: string) => {
    onUpdateViewableColumns({
      ...viewableColumns,
      [columnName]: {
        ...viewableColumns[columnName],
        isInView: !viewableColumns[columnName].isInView,
      },
    })
  }

  const handleClose = () => {
    onClose()
  }

  return (
    <IWModal open={isOpen} onClose={handleClose}>
      <IWModalHeader>
        <IWTypography size="lg" weight="medium">
          {t('insightManager.rawDataTable.modal.manageColumns')}
        </IWTypography>
      </IWModalHeader>
      <StyledIWModalContent>
        <StyledModalToggleTextButtons>
          <IWButton
            variant="anchorMain"
            color="primary"
            onClick={() => handleToggleAll(false)}
          >
            {t('insightManager.rawDataTable.modal.toggleOff')}
          </IWButton>
          <span>/</span>
          <IWButton
            variant="anchorMain"
            color="primary"
            onClick={() => handleToggleAll(true)}
          >
            {t('insightManager.rawDataTable.modal.toggleOn')}
          </IWButton>
        </StyledModalToggleTextButtons>

        <IWToggleGroup>
          {columns.map((e) => {
            const key = e.accessor
            const item = viewableColumns[key]
            const name = item?.title ?? 'N/A'
            const isInView = item?.isInView ?? false
            const disableToggle = item?.disableToggle ?? false

            return (
              <IWToggle
                key={key}
                id={key}
                label={name}
                name={key}
                checked={isInView}
                disabled={disableToggle}
                onChange={() => handleColumnToggle(key)}
              />
            )
          })}
        </IWToggleGroup>
      </StyledIWModalContent>

      <IWModalFooter>
        <StyledActions>
          <IWButton onClick={handleClose} variant="outline" color="grey">
            {t('button.cancel')}
          </IWButton>
          <IWButton
            onClick={() => onConfirm(viewableColumns)}
            variant="main"
            color="primary"
          >
            {t('button.confirm')}
          </IWButton>
        </StyledActions>
      </IWModalFooter>
    </IWModal>
  )
}

export function useIWGenericTable<T>(
  initialData: T[] = [],
): UseIWGenericTableHook<T> {
  const [currentItems, setCurrentItems] = useState<T[]>(initialData)
  const [currentPage, setCurrentPage] = useState(1)
  const [currentItemsPerPage, setCurrentItemsPerPage] = useState(10)
  const [currentItemsInView, setCurrentItemsInView] = useState<T[]>(() =>
    initialData.slice(0, 10),
  )

  const getCurrentSlice = useCallback(
    (items: T[]) => {
      const startIndex = (currentPage - 1) * currentItemsPerPage
      const endIndex = startIndex + currentItemsPerPage
      return items.slice(startIndex, endIndex)
    },
    [currentPage, currentItemsPerPage],
  )

  const setItemsInView = useCallback(
    (items: T[]) => {
      setCurrentItemsInView(getCurrentSlice(items))
    },
    [getCurrentSlice],
  )

  const setTablePageNumber = useCallback((pageNumber: number) => {
    setCurrentPage(pageNumber)
  }, [])

  const setTableItemsPerPage = useCallback((itemsPerPage: number) => {
    setCurrentItemsPerPage(itemsPerPage)
  }, [])

  const setItems = useCallback(
    (items: T[]) => {
      setCurrentItems(items)
      setItemsInView(items)
    },
    [setItemsInView],
  )

  useEffect(() => {
    setItemsInView(currentItems)
  }, [currentPage, currentItemsPerPage, currentItems, setItemsInView])

  return {
    currentPage,
    itemsPerPage: currentItemsPerPage,
    itemsInView: currentItemsInView,
    setItemsPerPage: setTableItemsPerPage,
    setCurrentPage: setTablePageNumber,
    setItemsInView,
    setTableItems: setItems,
  }
}

export default function IWGenericTable<T>({
  border = false,
  items = [],
  columns = [],
  isLoading = false,
  pinnedColumns = {},
  initialHiddenColumns = [],
  showColumnSettings = false,
  showEmptyMessage = false,
  topRightComponent: TopRightComponent,
  onDownload,
  onTableItemsUpdated,
}: GenericTableProps<T>) {
  const { t } = useTranslation()
  const { timezone } = useContext(LayoutContext)
  const [isColumnMenuOpen, setIsColumnMenuOpen] = useState(false)
  const [isFilterMenuOpen, setIsFilterMenuOpen] = useState(false)
  const [dataToShow, setDataToShow] = useState(items)
  const [updatedColumns, setUpdatedColumns] = useState(
    [] as GenericTableColumn[],
  )
  const [viewableColumns, setViewableColumns] = useState<
    ColumnIsInView | undefined
  >({})
  const [debouncer] = useState<{ timeout: NodeJS.Timeout | null }>({
    timeout: null,
  })

  const paginationOptions = [
    { value: 10, label: '10' },
    { value: 20, label: '20' },
    { value: 50, label: '50' },
    { value: 100, label: '100' },
  ]
  const [sort, setSort] = useState<
    | {
        dimension: string
        direction: SortDirection
      }
    | undefined
  >()

  const initialColumns = columns.reduce((memo, current) => {
    return {
      ...memo,
      [current.accessor]: {
        ...current,
        isInView: !initialHiddenColumns.includes(current.accessor),
        disableToggle: !!current.disableToggle,
      },
    }
  }, {} as ColumnIsInView)

  const {
    currentPage,
    itemsPerPage,
    itemsInView,
    setItemsPerPage,
    setCurrentPage,
    setTableItems,
  } = useIWGenericTable<T>(dataToShow)

  const {
    filtersState,
    filtersInView,
    handleFilterAdd,
    handleFilterRemove: onFilterRemove,
    setDefaultFilters,
    handleFilterClose,
    handleFilterOpen,
    setDefaultOptions,
    handleFilterConfirm: onFilterConfirm,
  } = useFilterBadges()

  const onChangePage = (num: number) => setCurrentPage(num)

  const onChangeItemsPerPage = (num: number) => {
    setItemsPerPage(num)
    setCurrentPage(1)
  }

  const handleFilterColumns = (inView: ColumnIsInView) => {
    setViewableColumns(inView)
    setUpdatedColumns(filterColumns(inView, columns) as GenericTableColumn[])
  }

  const handleSettingsClose = () => {
    setIsColumnMenuOpen(false)
  }

  const handleSettingsOpen = () => {
    setIsColumnMenuOpen(true)
  }

  const handleColumnSettingsConfirm = (inView: ColumnIsInView) => {
    handleFilterColumns(inView)
    setIsColumnMenuOpen(false)
  }

  const handleFilterConfirm = (id, s) => {
    onFilterConfirm(id, s)
    setCurrentPage(1)
  }

  const handleFilterRemove = (id: string) => {
    onFilterRemove(id)
  }

  const handleGetOptions = (columnName: string) => {
    const top = getUniqueValuesByKey(items, columnName)
    setDefaultOptions(columnName, top as string[])
  }

  const renderFilterMenu = (cols: GenericTableColumn[]) => {
    const menuItems = cols
      .filter((col) => col.filterType)
      .map((col) => ({
        title: col.title,
        key: col.accessor,
        type: col.filterType,
      }))

    return Object.entries(menuItems).map((elem) => (
      <IWContextMenuItem
        as="button"
        key={elem[1].key}
        label={elem[1].title}
        onClick={() => {
          if (elem[1].type === 'string') {
            handleGetOptions(elem[1].key)
          }
          handleFilterAdd(elem[1].key)
          setIsFilterMenuOpen(false)
        }}
      />
    ))
  }

  const handleSearchTextOptions = (columnName: string, searchValue: string) => {
    if (debouncer.timeout) {
      clearTimeout(debouncer.timeout)
    }
    const debouncedFn = new Promise<DropdownValueProps[]>((resolve) => {
      debouncer.timeout = setTimeout(async () => {
        const searchOptions = getUniqueValuesByKey(
          items,
          columnName,
          searchValue,
        )
        const dropdownOptions = searchOptions.map((option) => ({
          value: option,
          label: option,
        }))
        resolve(dropdownOptions as any)
      }, DEBOUNCE_TIME)
    })
    return debouncedFn
  }

  useEffect(() => {
    setTableItems(dataToShow)
  }, [dataToShow])

  useEffect(() => {
    if (Object.keys(initialColumns).length > 0) {
      handleFilterColumns(initialColumns)
    } else {
      setUpdatedColumns(columns)
    }

    const menuItems = columns
      .filter((col) => col.filterType)
      .map((col) => ({
        title: col.title,
        key: col.accessor,
        type: col.filterType,
      }))

    const defaultFilters: UseFilterBadgesHookProps = {}
    Object.entries(menuItems).forEach((el) => {
      defaultFilters[el[1].key] = {
        type: el[1].type as any,
        isInView: false,
        isOpen: false,
      }
    })

    setDefaultFilters(defaultFilters)
  }, [columns])

  useEffect(() => {
    const filterDataInView = applyFilters(items, filtersState, timezone)
    setTableItems(filterDataInView)
    setDataToShow(filterDataInView)

    if (onTableItemsUpdated) {
      onTableItemsUpdated(filterDataInView)
    }
  }, [filtersState, items])

  if (showEmptyMessage) {
    return <TableEmptyMessage message={t('messages.noItemsFound')} />
  }

  return (
    <StyledTableWrapper>
      {Boolean(columns.length) &&
        Boolean(columns.filter((col) => col.filterType).length) && (
          <StyledFilterWrapper>
            <IWContextMenu
              positions={['bottom']}
              align="start"
              menu={renderFilterMenu(columns)}
              isOpen={isFilterMenuOpen}
              onClickOutside={() => setIsFilterMenuOpen(false)}
            >
              <IWButton
                color="grey"
                variant="outline"
                icon={faFilter}
                iconPosition="leading"
                onClick={() => setIsFilterMenuOpen(true)}
              >
                {t('button.filters')}
              </IWButton>
            </IWContextMenu>

            {TopRightComponent ? TopRightComponent : <></>}
          </StyledFilterWrapper>
        )}

      <StyledFilterTextWrapper active={Boolean(filtersInView.length)}>
        {Boolean(filtersInView.length) && (
          <IWFilterBadgesGroup label={t(`filterBadgesGroup.appliedFilters`)}>
            {filtersInView.map((col) => {
              const { name, type, isOpen } = col

              if (type === 'datetime') {
                return (
                  <IWFilterBadgesDatetime
                    id={name}
                    key={name}
                    onConfirm={handleFilterConfirm}
                    onClick={() => handleFilterOpen(name)}
                    onCancel={handleFilterClose}
                    isOpen={isOpen}
                    disabledRelativeOptions={[
                      'firstDateAvailable',
                      'lastDateAvailable',
                    ]}
                    disabledMainOptions={[
                      'next',
                      'after',
                      'before',
                      'previous',
                    ]}
                    onRemove={handleFilterRemove}
                  />
                )
              }

              if (type === 'string') {
                return (
                  <IWFilterBadgesOptions
                    id={name}
                    key={name}
                    onClick={() => handleFilterOpen(name)}
                    isOpen={isOpen}
                    onConfirm={handleFilterConfirm}
                    onRemove={handleFilterRemove}
                    onCancel={handleFilterClose}
                    defaultOptions={
                      col.defaultOptions?.map((e) => ({
                        label: e,
                        value: e,
                      })) || []
                    }
                    onGetOptions={async (inputValue) => {
                      return handleSearchTextOptions(name, inputValue)
                    }}
                  />
                )
              }

              if (type === 'number') {
                return (
                  <IWFilterBadgesRange
                    id={name}
                    key={name}
                    onConfirm={handleFilterConfirm}
                    onCancel={handleFilterClose}
                    onClick={() => handleFilterOpen(name)}
                    isOpen={isOpen}
                    onRemove={handleFilterRemove}
                  />
                )
              }

              return <></>
            })}
          </IWFilterBadgesGroup>
        )}
      </StyledFilterTextWrapper>

      <IWTable
        border={border}
        isLoading={isLoading}
        data={itemsInView}
        columns={updatedColumns}
        pinnedColumns={pinnedColumns}
        sortedBy={sort ? { [sort.dimension]: sort.direction } : undefined}
        onSort={(dimension, direction) => {
          if (direction === null) {
            setSort(undefined)
          } else {
            const sortDirection = direction === 'ascending' ? 'asc' : 'desc'
            const sorted = orderBy(dataToShow, [dimension], [sortDirection])

            setTableItems(sorted)
            setSort({
              dimension,
              direction,
            })
          }
        }}
      />

      <PaginationWrapper hasSettings={showColumnSettings}>
        {showColumnSettings && (
          <IWButtonGroup color="primary" variant="outline" hasSolidBackground>
            <IWButton
              icon={faSlidersSimple}
              iconPosition="leading"
              disabled={isLoading}
              onClick={handleSettingsOpen}
            >
              {!onDownload && t('button.view')}
            </IWButton>
            {onDownload ? (
              <IWButton
                icon={faDownload}
                iconPosition="leading"
                disabled={isLoading}
                onClick={() => onDownload(items)}
              >
                {''}
              </IWButton>
            ) : (
              <></>
            )}
          </IWButtonGroup>
        )}
        <IWPaginationControls
          perPageLabel={t('tables.rowsPerPage')}
          itemsPerPage={itemsPerPage}
          perPageOptions={paginationOptions}
          totalItems={dataToShow.length}
          currentPage={currentPage}
          onChangePage={onChangePage}
          onChangeItemsPerPage={onChangeItemsPerPage}
        />
      </PaginationWrapper>

      {viewableColumns && (
        <ColumnSelector
          isOpen={isColumnMenuOpen}
          onClose={handleSettingsClose}
          onConfirm={handleColumnSettingsConfirm}
          initialColumns={initialColumns}
          viewableColumns={viewableColumns}
          columns={columns}
          onUpdateViewableColumns={(inViewColumns: ColumnIsInView) =>
            setViewableColumns(inViewColumns)
          }
        />
      )}
    </StyledTableWrapper>
  )
}
